// schema
import * as ISchema from "@schema";

// libraries
import * as React from "react";
import { createFragmentContainer, QueryRenderer, ReadyState } from "react-relay";
import withStyles from "@material-ui/core/styles/withStyles";
import { connect } from "react-redux";
import { Button } from "@material-ui/core";

// components
import { TeamJoinCreate } from "@components/team-join-create";
import {
  OnboardingTeamJoinCreateDisplayPane,
  OnboardingTeamJoinCreateSingleTeamConfirmation,
} from "@components/onboarding-nux/onboarding-steps/onboarding-team-join-create-step/OnboardingTeamJoinCreateStep";
import { OnboardingButtons } from "@components/onboarding-nux/onboarding-buttons";
import { OnboardingSplitScreenStep } from "@components/onboarding-nux/onboarding-steps/onboarding-split-screen-step";
import { LogoClock } from "@clockwise/web-commons/src/components/logo";
import { WebAppNavAvatarMenu } from "@components/web-app-nav";
import { SettingsDialog } from "@components/settings-dialog";
import { A, P } from "@components/ui";
import { Loader } from "@clockwise/web-commons/src/components/loader";

// state
import { setSettingsDialogOpen } from "@actions/chrome-extension.actions";
import { getOnboardingFlowState } from "@state/onboarding";
import { IReduxState } from "@reducers/root.reducer";

// util
import { getCurrentOrg } from "@util/org.util";
import { track, TrackingEvents, trackFSEvent } from "@util/analytics.util";
import { fromGlobalId } from "@clockwise/client-commons/src/util/graphql";
import { isFlagSet, UserFlags } from "@clockwise/client-commons/src/util/flags";
import { windowLocation } from "@util/location.util";

// constants
import { gcalUrl } from "@constants/site.constant";

// styles
import { colors } from "@clockwise/web-commons/src/styles/color.styles";

// routing
import { withRouteQuery } from "@routing/routes.util";

// internal
import { IContainer, IProps, IState } from "./WebAppTeams.d";
import { fragments, rendererQuery, query as webAppTeamsQuery } from "./WebAppTeams.gql";
import { styles } from "./WebAppTeams.styles";

export class WebAppTeamsComponent extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    this.state = {
      currentView: "create",
      joinMode: "off",
      isValid: false,
      saving: false,
      mode: "onboarding",
      teamName: "",
      isFinishedSaving: false,
    };
  }

  /////////////
  // Helpers //
  /////////////

  private getSingleTeamFromOrg = (org: ISchema.IOrg | null) => {
    return (
      (org &&
        org.userTeams &&
        org.userTeams.__typename !== "GraphEntityError" &&
        org.userTeams.list.length !== 0 &&
        org.userTeams.list[0]) ||
      undefined
    );
  };

  private getForwardButtonLabel = () => {
    const { viewer } = this.props;
    const { joinMode, currentView } = this.state;
    const org = getCurrentOrg(viewer);

    if (joinMode === "single-team" && this.getSingleTeamFromOrg(org)) {
      return "Got It";
    } else if (joinMode === "select-primary") {
      return "Confirm";
    } else if (currentView === "create") {
      return "Create Team";
    }
    return "Join Team";
  };

  private shouldShowNavAvatar = () => {
    const { mode, joinMode, isFinishedSaving } = this.state;

    return (mode === "onboarding" && joinMode === "single-team") || isFinishedSaving;
  };

  private isForwardButtonDisabled = () => {
    const { viewer } = this.props;
    const { joinMode } = this.state;
    const org = getCurrentOrg(viewer);

    if (joinMode === "single-team" && this.getSingleTeamFromOrg(org)) {
      return false;
    }

    return !this.state.isValid || this.state.saving;
  };

  private onPreSave = () => {};

  private onSave = () => {
    this.setState({
      isFinishedSaving: true,
    });
  };

  private goForward = () => {
    if (this.state.joinMode === "single-team") {
      return;
    }

    this.setState({
      saving: true,
    });
  };

  private onClickDone = () => {
    const { viewer } = this.props;

    const flagsErrorable = viewer.user.chromeExtensionFlagsErrorable;

    track(TrackingEvents.TEAMS_MIGRATION.CLICKED_DONE);

    if (isFlagSet(UserFlags.Onboarding.HasInstalledChromeExtension.name, flagsErrorable)) {
      windowLocation.assign("WebAppTeamsClickDone", gcalUrl);
    } else {
      this.props.router.push("/app?teams-migration#/week");
    }
  };

  private onClickSettings = () => {
    track(TrackingEvents.TEAMS_MIGRATION.CLICKED_SETTINGS);
    this.props.setSettingsDialogOpen(true);
  };

  private onBypass = () => {
    track(TrackingEvents.TEAMS_MIGRATION.SINGLE_TEAM_CONFIRMATION_BYPASSED);

    this.setState({
      mode: "settings",
      forcedInitialView: "create",
    });
  };

  public onInit = ({
    nDisplayedTeams,
    nSuggestedTeams,
    nDisplayedInviteSuggestions,
  }: {
    nDisplayedTeams: number;
    nSuggestedTeams: number;
    nDisplayedInviteSuggestions: number;
  }) => {
    const { viewer } = this.props;

    const org = getCurrentOrg(viewer);

    const userTeams =
      (org?.userTeams?.__typename !== "GraphEntityError" && org?.userTeams.list) || [];

    track(TrackingEvents.TEAMS_MIGRATION.SHOWN, {
      nDisplayedTeams,
      nSuggestedTeams,
      nDisplayedInviteSuggestions,
      isInvited: userTeams.length > 0,
      nInvitedTeams: userTeams.length,
    });
  };

  ///////////////
  // Lifecycle //
  ///////////////

  public maybeReRoute = (props: IProps) => {
    const { viewer } = props;
    const org = getCurrentOrg(viewer);
    const onboardingState = getOnboardingFlowState(viewer.user);

    if (!org) {
      this.props.router.push("/app");
    } else if (!onboardingState || onboardingState.current.state !== "Finished") {
      this.props.router.push("/app/onboarding");
    }
  };

  public componentDidMount() {
    this.maybeReRoute(this.props);

    trackFSEvent(TrackingEvents.TEAMS_MIGRATION.SHOWN);
  }

  public UNSAFE_componentWillReceiveProps(nextProps: IProps) {
    this.maybeReRoute(nextProps);
  }

  ///////////////
  // Rendering //
  ///////////////

  private renderComplete = () => {
    const { classes } = this.props;
    const { currentView, joinMode } = this.state;

    return (
      <div className={classes.singleContainer}>
        <div className={classes.emoji}>🙌</div>
        <div className={`${classes.header} single-team`}>
          {currentView === "create" || joinMode === "normal"
            ? "Your team is all set!"
            : "Welcome to the team!"}
        </div>
        <P style={{ marginBottom: 26, ...styles.singleParagraph }}>
          {currentView === "create"
            ? `You've successfully set up your team "${this.state.teamName}," `
            : joinMode === "select-primary"
            ? `You've set "${this.state.teamName}" to be your primary team, `
            : `You've successfully joined the team "${this.state.teamName}," `}{" "}
          you can view or manage your team anytime in <A onClick={this.onClickSettings}>settings</A>
          .
        </P>
        <Button
          className={classes.doneButton}
          onClick={this.onClickDone}
          variant="contained"
          color="primary"
        >
          Done
        </Button>
      </div>
    );
  };

  private renderDisplay = () => {
    return <OnboardingTeamJoinCreateDisplayPane />;
  };

  private renderSingleTeamConfirmation(
    team: ISchema.ITeam,
    viewer?: ISchema.IViewer,
    org?: ISchema.IOrg,
  ) {
    if (!viewer || !org) {
      return null;
    }

    return (
      <OnboardingTeamJoinCreateSingleTeamConfirmation
        team={team}
        viewer={viewer}
        org={org}
        onBypass={this.onBypass}
        onClickDone={this.onClickDone}
        onClickSettings={this.onClickSettings}
      />
    );
  }

  private renderConfigurationPane(
    viewer?: ISchema.IViewer,
    org?: ISchema.IOrg,
  ): JSX.Element | null {
    if (!viewer || !org) {
      return null;
    }

    return (
      <TeamJoinCreate
        org={org}
        viewer={viewer}
        mode={this.state.mode}
        onChangeValidity={(isValid) => this.setState({ isValid })}
        onChangeView={(currentView) => this.setState({ currentView })}
        onChangeTeamName={(teamName) => this.setState({ teamName })}
        onChangeJoinMode={(joinMode) => {
          if (joinMode === "single-team") {
            const singleTeam = this.getSingleTeamFromOrg(org);

            const teamId = singleTeam?.teamId;

            const youCreated = singleTeam?.createdBy?.value === fromGlobalId(viewer.user.id).id;

            track(TrackingEvents.TEAMS_MIGRATION.SHOWN, { teamId, userWasCreator: youCreated });
          }

          this.setState({ joinMode });
        }}
        analyticsMode="migration"
        onSave={this.onSave}
        onPreSave={this.onPreSave}
        save={this.state.saving}
        forcedInitialView={this.state.forcedInitialView}
      />
    );
  }

  private renderButtons(): JSX.Element | null {
    if (this.shouldShowNavAvatar()) {
      return null;
    }

    return (
      <OnboardingButtons
        backButtonHidden={true}
        forwardButtonLabel={this.getForwardButtonLabel()}
        forwardButtonLoading={this.state.saving}
        forwardButtonDisabled={this.isForwardButtonDisabled()}
        onClickBackButton={() => null}
        onClickForwardButton={this.goForward}
      />
    );
  }

  public render() {
    const { joinMode } = this.state;
    const { classes, viewer } = this.props;

    const org = getCurrentOrg(viewer);

    if (!org) {
      return null;
    }

    const pillsClassName =
      ((joinMode === "off" || joinMode === "select-primary") && " no-pills") || "";

    const showNavAvatar = this.shouldShowNavAvatar();

    return (
      <React.Fragment>
        <div className={classes.fixedContainer}>
          <div className="inner-container">
            <div className="nav-pane">
              <div className="nav-absolute">
                <div className="nav-items">
                  <div className="logo-wrapper">
                    <LogoClock fill={colors.blueDusk.standard} size={36} />
                  </div>
                  <div className="close-icon">
                    {showNavAvatar ? (
                      <WebAppNavAvatarMenu hideAdvancedMenu viewer={this.props.viewer} />
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
            <div className="split-step-wrapper">
              <QueryRenderer
                variables={{
                  orgRelayId: org.id,
                  myCalendarIds: [org.primaryOrgCalendar],
                }}
                query={rendererQuery}
                environment={this.props.relay.environment}
                render={(readyState: ReadyState) => {
                  const rendererViewer: ISchema.IViewer =
                    readyState.props && readyState.props.viewer;
                  const rendererOrg = getCurrentOrg(rendererViewer) || undefined;

                  if (!rendererViewer || !rendererOrg) {
                    return <Loader />;
                  }

                  if (this.state.isFinishedSaving) {
                    return this.renderComplete();
                  }

                  const singleTeam = this.getSingleTeamFromOrg(rendererOrg);
                  if (
                    this.state.joinMode === "single-team" &&
                    this.state.mode === "onboarding" &&
                    singleTeam
                  ) {
                    return this.renderSingleTeamConfirmation(
                      singleTeam,
                      rendererViewer,
                      rendererOrg,
                    );
                  }

                  return (
                    <OnboardingSplitScreenStep
                      displayPaneClassName={classes.displayPane}
                      configurationPaneChildren={this.renderConfigurationPane(
                        rendererViewer,
                        rendererOrg,
                      )}
                      configurationPaneClassName={`${classes.configurationPane}${pillsClassName}`}
                      onboardingMeta={this.props.onboardingMeta}
                    >
                      {this.renderDisplay()}
                    </OnboardingSplitScreenStep>
                  );
                }}
              />
            </div>
            {this.renderButtons()}
          </div>
        </div>
        {this.props.settingsDialogOpen ? (
          <SettingsDialog
            environment={this.props.relay.environment}
            onClose={() => this.props.setSettingsDialogOpen(false)}
          />
        ) : null}
      </React.Fragment>
    );
  }
}

///////////
// Redux //
///////////

function mapStateToProps(state: IReduxState, _ownProps: IContainer) {
  return {
    settingsDialogOpen: state.webExtension.settingsDialogOpen,
    onboardingMeta: state.onboarding.onboardingMeta,
  };
}

const WebAppTeamsWithRedux = connect(mapStateToProps, {
  setSettingsDialogOpen,
})(WebAppTeamsComponent);

/////////////////
// With Styles //
/////////////////

export const WebAppTeamsWithStyles = withStyles(styles)(WebAppTeamsWithRedux);

///////////
// Relay //
///////////

export const WebAppTeams = createFragmentContainer(WebAppTeamsWithStyles, fragments);

export const WebAppTeamsPage = withRouteQuery(WebAppTeams, webAppTeamsQuery);
